"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RichTextEditor = void 0;
/*
  RichTextEditor heavily relies on TipTap (https://github.com/ueberdosis/tiptap) for development. Due to complications importing their CommonJs exports
  across our various build pipelines, we need to dynamically import the modules.
*/
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
const Box_1 = __importDefault(require("@mui/material/Box"));
const CircularProgress_1 = __importDefault(require("@mui/material/CircularProgress"));
const Divider_1 = __importDefault(require("@mui/material/Divider"));
const react_1 = __importStar(require("react"));
const EditorToolbar_1 = __importDefault(require("./controls/EditorToolbar"));
const Link_1 = __importDefault(require("../Link/Link"));
const Typography_1 = require("../Typography/Typography");
const utils_1 = require("./utils");
const RichTextEditor = (args) => {
    return (react_1.default.createElement(react_1.Suspense, { fallback: react_1.default.createElement(CircularProgress_1.default, null) },
        react_1.default.createElement(DynamicRichTextEditor, { ...args })));
};
exports.RichTextEditor = RichTextEditor;
const loadTipTapModules = async () => {
    const [{ Editor, Extension, Node, mergeAttributes }, { default: Paragraph }, { default: TextAlign }, { default: Underline }, { EditorContent, NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer, useEditor }, { default: StarterKit },] = await Promise.all([
        import('@tiptap/core'),
        import('@tiptap/extension-paragraph'),
        import('@tiptap/extension-text-align'),
        import('@tiptap/extension-underline'),
        import('@tiptap/react'),
        import('@tiptap/starter-kit'),
    ]);
    return {
        Editor,
        EditorContent,
        Extension,
        Link: buildLink(Node, NodeViewWrapper, ReactNodeViewRenderer, mergeAttributes),
        Node,
        Paragraph: buildExtendedParagraph(NodeViewContent, NodeViewWrapper, Paragraph, ReactNodeViewRenderer, mergeAttributes),
        StarterKit,
        TextAlign,
        Underline,
        useEditor,
    };
};
const buildExtendedParagraph = (NodeViewContent, NodeViewWrapper, Paragraph, ReactNodeViewRenderer, mergeAttributes) => {
    return Paragraph.extend({
        addAttributes() {
            return {
                variant: (element) => element.getAttribute('variant') ?? 'body1',
            };
        },
        renderHTML(props) {
            return ['p', mergeAttributes(props.HTMLAttributes), 0];
        },
        addNodeView() {
            return ReactNodeViewRenderer((props) => {
                const { textAlign, variant } = props.node.attrs;
                return (react_1.default.createElement(NodeViewWrapper, null,
                    react_1.default.createElement(Typography_1.Typography, { align: textAlign ?? 'left', component: "div", variant: variant ?? 'body1' },
                        react_1.default.createElement(NodeViewContent, null))));
            });
        },
    });
};
const buildLink = (Node, NodeViewWrapper, ReactNodeViewRenderer, mergeAttributes) => {
    return Node.create({
        name: 'medelyLink',
        group: 'inline',
        inline: true,
        addAttributes() {
            return {
                href: (element) => element.getAttribute('href'),
                text: (element) => element.getAttribute('text'),
            };
        },
        parseHTML() {
            return [
                {
                    tag: 'link',
                },
            ];
        },
        renderHTML(props) {
            return ['link', mergeAttributes(props.HTMLAttributes), 0];
        },
        addNodeView() {
            return ReactNodeViewRenderer((props) => {
                const { href, text } = props.node.attrs ?? {};
                return (react_1.default.createElement(NodeViewWrapper, { as: "span" },
                    react_1.default.createElement(Link_1.default, { external: true, href: href }, text)));
            });
        },
    });
};
let cache = {};
let errorsCache = {};
const useSuspense = (importPromise, cacheKey) => {
    const cachedModule = cache[cacheKey];
    // already loaded previously
    if (cachedModule) {
        return cachedModule;
    }
    //prevents import() loop on failed imports
    if (errorsCache[cacheKey]) {
        throw errorsCache[cacheKey];
    }
    // gets caught by Suspense
    throw importPromise
        .then((mod) => (cache[cacheKey] = mod))
        .catch((err) => {
        console.log(err);
        errorsCache[cacheKey] = err;
    });
};
const DynamicRichTextEditor = (args) => {
    const { disabled, readOnly, input, value } = args;
    const tiptapModules = useSuspense(loadTipTapModules(), 'loading-tiptap');
    const editor = tiptapModules.useEditor({
        editable: !(readOnly || disabled),
        extensions: [
            tiptapModules.Link,
            tiptapModules.Paragraph,
            tiptapModules.TextAlign.configure({ types: ['paragraph'] }),
            tiptapModules.StarterKit.configure({ paragraph: false }),
            tiptapModules.Underline,
        ],
        onDestory: () => {
            cache = {};
            errorsCache = {};
        },
        onFocus: ({ event }) => {
            input?.onFocus(event);
        },
        onUpdate: ({ editor }) => {
            input?.onChange(editor.isEmpty ? undefined : editor.getJSON());
        },
    }, [readOnly, disabled]);
    // // In order to sync external content updates with editor state, updates need to be wrapped in setTimeout
    // // Related issue: https://github.com/ueberdosis/tiptap/issues/3764
    (0, react_1.useEffect)(() => {
        setTimeout(() => {
            editor?.commands.setContent((0, utils_1.parseValue)(input?.value) ?? (0, utils_1.parseValue)(value));
        });
    }, [editor, value]);
    return (react_1.default.createElement(react_1.default.Fragment, null,
        !readOnly && editor && (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(Box_1.default, { sx: { margin: '0.5rem 0', display: 'flex', width: 'fit-content' } },
                react_1.default.createElement(EditorToolbar_1.default, { editor: editor, disabled: !!disabled })),
            react_1.default.createElement(Divider_1.default, null))),
        react_1.default.createElement(Box_1.default, { "data-testid": "rich-text-content", sx: {
                '.ProseMirror:focus': {
                    outline: 'none',
                },
            } },
            react_1.default.createElement(tiptapModules.EditorContent, { editor: editor }))));
};
exports.default = RichTextEditor;
